import type React from "react";
import { createContext, useContext } from "react";

import { IS_CHROME_EXTENSION } from "@src/env";

/**
 * Context that stores whether the app is running inside the Chrome extension.
 * It defaults to the value derived from environment variables, but can be
 * overridden via the `IsChromeExtensionProvider` for Storybook and tests.
 */
const IsChromeExtensionContext = createContext<boolean>(IS_CHROME_EXTENSION);

export interface IsChromeExtensionProviderProps {
  /**
   * Explicit value for chrome-extension mode. If omitted, the provider falls
   * back to the value detected from environment variables at build time.
   */
  isChromeExtension?: boolean;
  children: React.ReactNode;
}

export const IsChromeExtensionProvider: React.FC<
  IsChromeExtensionProviderProps
> = ({ children, isChromeExtension }) => {
  return (
    <IsChromeExtensionContext.Provider
      value={isChromeExtension ?? IS_CHROME_EXTENSION}
    >
      {children}
    </IsChromeExtensionContext.Provider>
  );
};

/**
 * Hook to access whether the application is running inside the Chrome
 * extension. Components should prefer this hook over importing the constant
 * from `env.ts` so that Storybook and tests can override the value easily.
 */
export function useIsChromeExtension(): boolean {
  return useContext(IsChromeExtensionContext);
}
